import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { withBack, readBack } from '../../../lib/backNav'
import { pdf } from '@react-pdf/renderer'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import { btnSecondary } from '../types'
import type { DeliveryNote } from '../types'
import { NotePDF } from './NotePDF'
import type { NoteData, NoteItem } from './NotePDF'
import { getCompanySettings } from '../../../lib/companySettings'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from '../useColumnPrefs'
import { SortableColHeader, ColumnToggleButton, computeAutoFitWidths } from '../ColDragHeader'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { usePagination } from '../../../hooks/usePagination'
import PaginationControls from '../../../components/PaginationControls'
import DispatchStatusBadge from '../../../components/DispatchStatusBadge'
import RowNotesDrawer from './RowNotesDrawer'
import { dispatchProgress, noteMatchesSource, type DispatchProgress } from '../../../lib/dispatchProgress'
import { classifyOverdue, OVERDUE_ROW_CLASS, OVERDUE_TEXT_CLASS, overdueBadgeText } from '../../../lib/overdue'

const INBOUND_COL_DEFS = [
  { id: 'job_ref',    label: 'Job Ref' },
  { id: 'house_refs', label: 'House Ref' },
  { id: 'destination', label: 'Destination' },
  { id: 'split',      label: 'Group' },
  { id: 'nm',         label: 'N/M' },
  { id: 'mode',       label: 'Mode' },
  { id: 'plts',       label: 'Plts' },
  { id: 'ctns',       label: 'Ctns' },
  { id: 'wt',         label: 'Wt (kg)' },
  { id: 'cbm',        label: 'CBM' },
  { id: 'note',       label: 'Note' },
  { id: 'status',     label: 'Status' },
  { id: 'dispatched', label: 'Dispatched' },
] as const

const INBOUND_AUTO_WIDTHS = computeAutoFitWidths(INBOUND_COL_DEFS)

// How far a row has got, from its notes' lifecycle (see dispatchProgress.ts):
//  action  = still needs someone: no note, only drafts, or not all of it scheduled yet
//  planned = everything is scheduled/collected but some still sits in Planned Outbound (goods still here)
//  done    = every carton on a COMPLETED note (goods have left)
// Only 'action' rows get the overdue warning; 'done' rows drop out of the default views.
type RowStage = 'action' | 'planned' | 'done'
const STAGE_RANK: Record<RowStage, number> = { action: 0, planned: 1, done: 2 }
function rowStage(p: DispatchProgress | undefined): RowStage {
  if (!p) return 'action'
  if (p.total > 0 && p.completed >= p.total) return 'done'
  if (p.planned > 0 && p.completed + p.planned >= p.total) return 'planned'
  return 'action'
}
type StatusFilter = 'action' | 'planned' | 'dispatched' | 'all' | 'pending' | 'partial' | 'ready' | 'overdue' | 'released_pending'

interface InboundItem {
  id: string
  warehouse_id: string
  devanning_id: string | null
  devanning_item_id: string | null
  job_ref: string | null
  split_no: string | null
  house_job_refs: string | null
  group_name: string | null
  destination: string | null
  hbl_no: string | null
  system_ref: string | null
  description: string | null
  delivery_address: string | null
  delivery_method: string | null
  delivery_contact: string | null
  customer_id: string | null
  cartons: number
  pallets_qty: number | null
  cbm: number | null
  kgs: number | null
  notes: string | null
  status: 'in_inbound' | 'released_pending' | 'planned' | 'outbound' | 'dispatched' | 'cancelled'
  dispatch_mode: 'delivery' | 'self_pickup' | 'supplier_pickup' | null
  dispatch_carrier: string | null
  dispatch_vehicle: string | null
  dispatch_planned_at: string | null
  dispatch_at: string | null
  dispatch_notes: string | null
  dispatch_by: string | null
  location_code: string | null
  inbound_at: string | null
  release_type: 'collection' | 'delivery' | null
  planned_release_at: string | null
  actual_release_at: string | null
  release_note_ref: string | null
  released_by: string | null
  collection_contact: string | null
  inv_notes: string | null
}

const STATUS_CHIP: Record<string, string> = {
  in_inbound:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  released_pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
}

const NOTE_CHIP = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200'

export default function InboundPage() {
  const { id }        = useParams<{ id: string }>()
  const { user } = useAuth()

  const [items,        setItems]        = useState<InboundItem[]>([])
  const [loading,      setLoading]      = useState(true)
  const [warehouseName, setWarehouseName] = useState('')
  // "Ready to Dispatch" = item's plan/split already has a draft Collection/Delivery
  // Note from the Pre-Devanning Plan stage. Draft rows key on plan_id + group_name,
  // not the inventory row directly, so this is a keyset built from a join.
  // Merged single-note model: the Note button reads the same delivery_notes table as the
  // Devannings > Notes page, keyed by item since a note isn't 1:1 with an item any more
  // (several notes per courier/day can cover overlapping items) -- only ACTIVE notes, since a
  // SUPERSEDED/VOID one shouldn't clutter the per-row dropdown (its full history is still
  // visible on the Notes page). Generating (new or reissue) always happens on that one page,
  // never here.
  // ACTIVE notes per devanning, plus every devanning_item's group -- an Inbound row is one
  // devanning group, and its notes/dispatched quantity are matched against ALL the group's items.
  const [activeNotes, setActiveNotes] = useState<DeliveryNote[]>([])
  const [groupItemIds, setGroupItemIds] = useState<Record<string, string[]>>({})
  // devanning_item id -> its group key as the Notes page keys it (group_name || split_no || id)
  const [groupKeyByItem, setGroupKeyByItem] = useState<Record<string, string>>({})
  // Incomplete badge: a job_ref where at least one devanning_item isn't yet covered by an
  // ACTIVE, COMPLETED-lifecycle note's item_ids -- see v_job_completion_status.
  const [incompleteJobRefs, setIncompleteJobRefs] = useState<Map<string, { done: number; total: number }>>(new Map())
  const [filterSearch, setFilterSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '')
  const [filterStatus, setFilterStatus] = useState<StatusFilter>(() => new URLSearchParams(window.location.search).get('status') === 'all' ? 'all' : 'action')

  type DialogType = 'cancel_item' | null
  const [dialog,     setDialog]    = useState<DialogType>(null)
  const [selItem,    setSelItem]   = useState<InboundItem | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [toast,      setToast]     = useState<string | null>(null)

  // ── Column prefs ───────────────────────────────────────────────────────────
  const { order: colOrder, hidden: colHidden, visibleCols, extra: colExtra,
    setOrder: setColOrder, toggleHidden, setExtra: setColExtra, reset: resetCols } =
    useColumnPrefs('col_pref_inbound', INBOUND_COL_DEFS, user?.id, undefined, { sortBy: 'inbound_at', sortDir: 'desc', widths: {} })
  const savedWidths = (colExtra.widths as Record<string, number>) ?? {}
  const getColW  = (id: string) => savedWidths[id] ?? INBOUND_AUTO_WIDTHS[id] ?? 100
  const saveColW = (id: string, w: number) => setColExtra({ ...colExtra, widths: { ...savedWidths, [id]: w } })
  const resetColW = (id: string) => { const n = { ...savedWidths }; delete n[id]; setColExtra({ ...colExtra, widths: n }) }
  const colSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handleColDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const oi = colOrder.indexOf(active.id as string)
    const ni = colOrder.indexOf(over.id as string)
    if (oi !== -1 && ni !== -1) setColOrder(arrayMove(colOrder, oi, ni))
  }

  // ── Sort ───────────────────────────────────────────────────────────────────
  type SortCol = 'job_ref' | 'house_refs' | 'destination' | 'split' | 'nm' | 'mode' | 'plts' | 'ctns' | 'wt' | 'cbm' | 'status' | 'dispatched' | 'inbound_at'
  const sortBy  = (colExtra.sortBy  as SortCol)  ?? 'inbound_at'
  const sortDir = (colExtra.sortDir as 'asc'|'desc') ?? 'desc'
  const toggleSort = (col: SortCol) => {
    const newDir = sortBy === col && sortDir === 'asc' ? 'desc' : 'asc'
    setColExtra({ ...colExtra, sortBy: col, sortDir: newDir })
  }
  const SORTABLE = new Set(['job_ref','house_refs','destination','split','nm','mode','plts','ctns','wt','cbm','status','dispatched'])

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchItems = useCallback(async () => {
    setLoading(true)
    const [{ data: wh }, { data: inv }] = await Promise.all([
      supabase.from('warehouses').select('name').eq('id', id!).single(),
      supabase.from('warehouse_inventory')
        .select('*')
        .eq('warehouse_id', id!)
        .in('status', ['in_inbound', 'released_pending'])
        .order('inbound_at', { ascending: false }),
    ])
    setWarehouseName((wh as any)?.name || '')
    const invItems = (inv as InboundItem[]) || []
    setItems(invItems)

    const devIds = [...new Set(invItems.map(i => i.devanning_id).filter(Boolean))] as string[]
    if (devIds.length > 0) {
      const { data: dNotes } = await supabase.from('delivery_notes').select('*')
        .in('devanning_id', devIds).eq('status', 'ACTIVE')
      setActiveNotes((dNotes as DeliveryNote[] | null) || [])
      const { data: dItems } = await supabase.from('devanning_items').select('id, devanning_id, group_name, split_no').in('devanning_id', devIds)
      const byGroup: Record<string, string[]> = {}
      const keyByItem: Record<string, string> = {}
      for (const di of (dItems as { id: string; devanning_id: string; group_name: string | null; split_no: string | null }[] | null) || []) {
        const key = di.group_name || di.split_no || di.id
        keyByItem[di.id] = key
        ;(byGroup[`${di.devanning_id}|${key}`] ??= []).push(di.id)
      }
      setGroupKeyByItem(keyByItem)
      setGroupItemIds(byGroup)

      const { data: completion } = await supabase.from('v_job_completion_status')
        .select('job_ref, total_items, completed_items').in('devanning_id', devIds)
      // Per job_ref (a job can span several devannings): rows whose goods have left the warehouse
      // vs all rows; only jobs with something still to go keep the badge.
      const agg = new Map<string, { done: number; total: number }>()
      for (const r of (completion as { job_ref: string; total_items: number; completed_items: number }[] | null) || []) {
        const a = agg.get(r.job_ref) ?? { done: 0, total: 0 }
        a.done += r.completed_items; a.total += r.total_items
        agg.set(r.job_ref, a)
      }
      setIncompleteJobRefs(new Map([...agg].filter(([, a]) => a.done < a.total)))
    } else {
      setActiveNotes([])
      setGroupItemIds({})
      setGroupKeyByItem({})
      setIncompleteJobRefs(new Map())
    }
    setLoading(false)
  }, [id])

  useEffect(() => { fetchItems() }, [fetchItems])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  // ── Dialog openers ─────────────────────────────────────────────────────────
  function openCancel(item: InboundItem) {
    setSelItem(item)
    setDialog('cancel_item')
  }
  // ── View / re-download note ────────────────────────────────────────────────
  async function downloadNote(item: InboundItem) {
    if (!item.release_note_ref) return
    const { data: noteData } = await supabase
      .from('release_notes').select('*').eq('note_ref', item.release_note_ref).single()
    if (!noteData) return
    const note = noteData as any
    const noteForPDF: NoteData = {
      company:            await getCompanySettings(),
      note_ref:           note.note_ref,
      note_type:          note.note_type,
      job_ref:            note.job_ref,
      customer_ref:       note.customer_ref ?? null,
      planned_release_at: note.planned_release_at,
      contact_name:       note.contact_name,
      contact_phone:      note.contact_phone,
      vehicle_reg:        note.vehicle_reg,
      driver_name:        note.driver_name,
      haulier:            note.haulier,
      delivery_address:   note.delivery_address,
      collect_from:       note.collect_from ?? null,
      marks_numbers:      note.marks_numbers ?? null,
      container_no:       note.container_no ?? null,
      seal_no:            note.seal_no ?? null,
      container_type:     note.container_type ?? null,
      remarks:            note.remarks ?? null,
      delivery_refs:      note.delivery_refs ?? [],
      notes:              note.notes,
      warehouse_name:     warehouseName,
      issued_at:          note.created_at,
      serial_no:          note.serial_no ?? null,
    }
    const pdfItem: NoteItem = {
      group_name:       item.group_name,
      description:      item.description,
      pallets_qty:      item.pallets_qty,
      cartons:          item.cartons,
      kgs:              item.kgs,
      cbm:              item.cbm,
      delivery_address: item.delivery_address,
    }
    const blob = await pdf(<NotePDF note={noteForPDF} items={[pdfItem]} />).toBlob()
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = `${note.note_ref}.pdf`; a.click()
    URL.revokeObjectURL(url)
  }

  // ── Cancel item ────────────────────────────────────────────────────────────
  async function submitCancel() {
    if (!selItem) return
    setSubmitting(true)
    await supabase.from('warehouse_inventory').update({ status: 'cancelled' }).eq('id', selItem.id)
    setDialog(null)
    await fetchItems()
    showToast(`✓ ${selItem.group_name || selItem.id} cancelled`)
    setSubmitting(false)
  }

  // ── Filter + sort ──────────────────────────────────────────────────────────
  // All devanning_item ids that make up an Inbound row (its group), falling back to the row's
  // own devanning_item_id when the group can't be resolved.
  // A row's group is resolved through its own devanning_item (not the inventory row's stored
  // group_name, which can go stale if the group was renamed after inbounding -- e.g. legacy
  // "Devan Split 1" vs the devanning's "Group 1").
  const rowGroupKey = useCallback((it: InboundItem): string =>
    (it.devanning_item_id && groupKeyByItem[it.devanning_item_id]) || it.group_name || '', [groupKeyByItem])
  const rowItemIds = useCallback((it: InboundItem): string[] => {
    const grp = it.devanning_id ? groupItemIds[`${it.devanning_id}|${rowGroupKey(it)}`] : undefined
    return grp?.length ? grp : (it.devanning_item_id ? [it.devanning_item_id] : [])
  }, [groupItemIds, rowGroupKey])
  const progressByRow = useMemo(() => {
    const m: Record<string, DispatchProgress> = {}
    for (const it of items) m[it.id] = dispatchProgress(it.cartons, activeNotes, rowItemIds(it))
    return m
  }, [items, activeNotes, rowItemIds])

  const stageByRow = useMemo(() => {
    const m: Record<string, RowStage> = {}
    for (const it of items) m[it.id] = rowStage(progressByRow[it.id])
    return m
  }, [items, progressByRow])

  const matchesStatusFilter = useCallback((it: InboundItem, f: StatusFilter): boolean => {
    const p = progressByRow[it.id]
    const stage = stageByRow[it.id]
    switch (f) {
      case 'action':     return stage === 'action' && it.status === 'in_inbound'
      case 'planned':    return stage === 'planned'
      case 'dispatched': return stage === 'done'
      case 'pending':    return it.status === 'in_inbound' && p?.noted === 0
      case 'partial':    return it.status === 'in_inbound' && !!p && p.noted > 0 && p.completed < p.total
      case 'ready':      return it.status === 'in_inbound' && stage !== 'done' && (p?.planned ?? 0) > 0
      case 'overdue':    return it.status === 'in_inbound' && stage === 'action' && !!it.inbound_at && (Date.now() - new Date(it.inbound_at).getTime()) > 7 * 86400_000
      case 'released_pending': return it.status === 'released_pending'
      default:           return true
    }
  }, [progressByRow, stageByRow])

  const visible = useMemo(() => {
    const filtered = items.filter(it => {
      if (!matchesStatusFilter(it, filterStatus)) return false
      if (filterSearch) {
        const q = filterSearch.toLowerCase()
        if (!(
          it.job_ref?.toLowerCase().includes(q) ||
          it.house_job_refs?.toLowerCase().includes(q) ||
          it.group_name?.toLowerCase().includes(q) ||
          it.system_ref?.toLowerCase().includes(q) ||
          it.hbl_no?.toLowerCase().includes(q) ||
          it.destination?.toLowerCase().includes(q) ||
          it.delivery_address?.toLowerCase().includes(q)
        )) return false
      }
      return true
    })
    const sortVal = (it: InboundItem) => {
      switch (sortBy) {
        case 'job_ref':      return it.job_ref ?? ''
        case 'house_refs':   return it.house_job_refs ?? ''
        case 'destination':  return it.delivery_address ?? ''
        case 'split':        return it.group_name ?? ''
        case 'nm':           return it.system_ref ?? ''
        case 'mode':         return it.delivery_method ?? ''
        case 'plts':         return it.pallets_qty ?? 0
        case 'ctns':         return it.cartons
        case 'wt':           return it.kgs ?? 0
        case 'cbm':          return it.cbm ?? 0
        case 'status':       return it.status
        case 'dispatched':   return progressByRow[it.id] ? progressByRow[it.id].dispatched / (progressByRow[it.id].total || 1) : 0
        default:             return it.inbound_at ?? ''
      }
    }
    // Stage first (needs-action rows on top, Planned below, finished last) even under a column
    // sort, so the rows that need chasing are never buried; the chosen sort orders within a stage.
    return [...filtered].sort((a, b) => {
      const byStage = STAGE_RANK[stageByRow[a.id]] - STAGE_RANK[stageByRow[b.id]]
      if (byStage) return byStage
      const av = sortVal(a), bv = sortVal(b)
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [items, filterStatus, filterSearch, sortBy, sortDir, progressByRow, stageByRow, matchesStatusFilter])

  const { scrollProps: ibScrollProps, contentRef: ibTableRef, bar: ibScrollBar } = useStickyHScroll([visible, visibleCols])

  // Z35-style pagination -- stat cards above read `items` (full/filtered, unpaginated) so
  // they're never affected by which page is showing; only the table body is sliced.
  const { page, setPage, pageSize, setPageSize } = usePagination('inbound-list')
  useEffect(() => { setPage(0) }, [filterSearch, filterStatus]) // eslint-disable-line react-hooks/exhaustive-deps
  const pagedTotal = visible.length
  const paged = pageSize === 'all' ? visible : visible.slice(page * pageSize, page * pageSize + pageSize)

  // Coming back from the Notes page (?row=<inventory id>, see withBack): jump to the page that
  // holds that row, scroll it into view and flash it -- instead of dumping staff at the top of
  // the list to search again.
  const [searchParams] = useSearchParams()
  const backTarget = readBack(searchParams)
  // ?item=<devanning_item_id> (from a note's Source link) -> the Inbound row whose group holds it.
  const itemParam = searchParams.get('item')
  const rowParam = useMemo(() => {
    const direct = searchParams.get('row')
    if (direct || !itemParam || loading) return direct
    return items.find(i => i.devanning_item_id === itemParam || rowItemIds(i).includes(itemParam))?.id ?? null
  }, [searchParams, itemParam, loading, items, rowItemIds])
  useEffect(() => {
    if (!loading && itemParam && !rowParam) showToast("That note's row is no longer on Inbound (released or cancelled).")
  }, [loading, itemParam, rowParam])
  const [highlightRow, setHighlightRow] = useState<string | null>(null)
  // The row whose notes drawer is open (also reopened by ?notes=1 when returning from an editor).
  const [drawerRowId, setDrawerRowId] = useState<string | null>(null)
  const jumpedRef = useRef<string | null>(null)
  useEffect(() => {
    if (loading || !rowParam || jumpedRef.current === rowParam) return
    const idx = visible.findIndex(i => i.id === rowParam)
    if (idx < 0) {
      // The row exists but the (default "Needs action") filter hides it, e.g. a Planned row reached
      // from a Source link -- widen to All rather than landing on a page without it.
      if (items.some(i => i.id === rowParam) && (filterStatus !== 'all' || filterSearch)) { setFilterStatus('all'); setFilterSearch('') }
      return
    }
    jumpedRef.current = rowParam
    if (pageSize !== 'all') setPage(Math.floor(idx / pageSize))
    setHighlightRow(rowParam)
    if (searchParams.get('notes') === '1') setDrawerRowId(rowParam)
    setTimeout(() => document.getElementById(`inbound-row-${rowParam}`)?.scrollIntoView({ block: 'center' }), 150)
    setTimeout(() => setHighlightRow(null), 4000)
  }, [loading, rowParam, visible, pageSize, setPage, items, filterStatus, filterSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalCtns = visible.reduce((s, i) => s + i.cartons, 0)
  const totalPlts = visible.reduce((s, i) => s + (i.pallets_qty ?? 0), 0)

  // ── Dashboard stats (all items, not just visible) ──────────────────────────
  const now = Date.now()
  // Driven by the notes' lifecycle_status, not by whether a note exists: "Dispatched" only means
  // COMPLETED (goods really left), so drafting or pushing a note doesn't move a row out of these.
  const countFor = (f: StatusFilter) => items.filter(i => matchesStatusFilter(i, f)).length
  const pendingDispatch = countFor('pending')
  const partiallyDispatched = countFor('partial')
  const readyDispatch = countFor('ready')
  const overdue7 = countFor('overdue')

  function daysSince(dt: string | null) {
    if (!dt) return null
    return Math.floor((now - new Date(dt).getTime()) / 86400_000)
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-green-600 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">
            {toast}
          </div>
        )}

        {backTarget && <Link to={backTarget.path} className="text-sm text-blue-600 hover:underline">← Back to {backTarget.label}</Link>}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Inbound</h2>
          <div className="flex gap-4 text-sm text-gray-500">
            <span>Showing: <strong className="text-gray-800 dark:text-gray-100 font-mono">{totalCtns}</strong> ctns</span>
            <span><strong className="text-gray-800 dark:text-gray-100 font-mono">{totalPlts}</strong> pallets</span>
          </div>
        </div>

        {/* ── Dashboard stats cards ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button type="button" onClick={() => setFilterStatus(filterStatus === 'pending' ? 'action' : 'pending')} title="Click to filter the list" className={`text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 ${filterStatus === 'pending' ? 'ring-2 ring-blue-400' : ''}`}>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">Pending Dispatch</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{pendingDispatch}</p>
            <p className="text-xs text-gray-400 mt-0.5">rows with no note yet</p>
          </button>
          <button type="button" onClick={() => setFilterStatus(filterStatus === 'partial' ? 'action' : 'partial')} title="Click to filter the list" className={`text-left rounded-xl border px-4 py-3 ${filterStatus === 'partial' ? 'ring-2 ring-blue-400' : ''} ${partiallyDispatched > 0 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
            <p className={`text-xs uppercase font-semibold tracking-wide mb-1 ${partiallyDispatched > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>Partially Dispatched</p>
            <p className={`text-3xl font-bold ${partiallyDispatched > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-white'}`}>{partiallyDispatched}</p>
            <p className="text-xs text-gray-400 mt-0.5">note raised, not all collected</p>
          </button>
          <button type="button" onClick={() => setFilterStatus(filterStatus === 'overdue' ? 'action' : 'overdue')} title="Click to filter the list" className={`text-left rounded-xl border px-4 py-3 ${filterStatus === 'overdue' ? 'ring-2 ring-blue-400' : ''} ${overdue7 > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
            <p className={`text-xs uppercase font-semibold tracking-wide mb-1 ${overdue7 > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              ⚠ Overdue 7+ Days
            </p>
            <p className={`text-3xl font-bold ${overdue7 > 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-white'}`}>{overdue7}</p>
            <p className={`text-xs mt-0.5 ${overdue7 > 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400'}`}>in_inbound &gt; 7 days</p>
          </button>
          <button type="button" onClick={() => setFilterStatus(filterStatus === 'ready' ? 'action' : 'ready')} title="Click to filter the list" className={`text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 ${filterStatus === 'ready' ? 'ring-2 ring-blue-400' : ''}`}>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">Ready to Dispatch</p>
            <p className="text-3xl font-bold text-green-700 dark:text-green-400">{readyDispatch}</p>
            <p className="text-xs text-gray-400 mt-0.5">has a Planned note, awaiting release</p>
          </button>
        </div>

        {/* Toolbar: filters + column toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Search</label>
            <input className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
              placeholder="Job ref / house ref / split / address…" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterStatus} onChange={e => setFilterStatus(e.target.value as StatusFilter)}>
              <option value="action">Needs action</option>
              <option value="planned">Planned</option>
              <option value="dispatched">Dispatched</option>
              <option value="all">All</option>
              {(['pending', 'partial', 'ready', 'overdue'] as const).includes(filterStatus as 'pending') && (
                <option value={filterStatus}>
                  {{ pending: 'No note yet', partial: 'Partially dispatched', ready: 'Ready to dispatch', overdue: 'Overdue 7+ days' }[filterStatus as 'pending']}
                </option>
              )}
              <option value="released_pending">Pending Release</option>
            </select>
          </div>
          {(filterSearch || filterStatus !== 'action') && (
            <button onClick={() => { setFilterSearch(''); setFilterStatus('action') }}
              className="text-xs text-gray-400 hover:text-gray-600 underline self-end pb-1.5">
              Clear
            </button>
          )}
          <div className="ml-auto self-end">
            <ColumnToggleButton cols={INBOUND_COL_DEFS} hidden={colHidden} onToggle={toggleHidden} onReset={resetCols}
              onRecalculate={() => { const w = computeAutoFitWidths(INBOUND_COL_DEFS); setColExtra({ ...colExtra, widths: { ...w, ...savedWidths } }) }} />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto" {...ibScrollProps}>
            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                No inbound items. Items are added when a devanning is pushed to inbound.
              </div>
            ) : (
              <table ref={ibTableRef as React.RefObject<HTMLTableElement>} className="table-fixed min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <DndContext sensors={colSensors} collisionDetection={closestCenter} onDragEnd={handleColDragEnd}>
                  <SortableContext items={visibleCols.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {visibleCols.map(col => (
                          <SortableColHeader key={col.id} id={col.id}
                            style={{ width: getColW(col.id), minWidth: '50px' }}
                            className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap"
                            onResizeEnd={w => saveColW(col.id, w)}
                            onResizeReset={() => resetColW(col.id)}
                            onClick={SORTABLE.has(col.id) ? () => toggleSort(col.id as SortCol) : undefined}>
                            {col.label}
                            {SORTABLE.has(col.id) && sortBy === col.id && (
                              <span className="ml-1 text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </SortableColHeader>
                        ))}
                        <th style={{ width: '90px' }} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Days</th>
                        {/* Z50.X fix: under table-fixed (needed so column drag-resize actually widens the
                            table instead of just shrinking siblings, see 1902b30), a column with no
                            explicit width gets whatever's left over rather than sizing to its own
                            content -- this is by far the widest cell (NoteDropdown + a Dispatch/Mark
                            Released button + cancel), so leaving it unsized was squeezing the whole table
                            to fit the viewport and silently preventing the horizontal overflow the sticky
                            scrollbar depends on. */}
                        <th style={{ width: '320px' }} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap" />
                      </tr>
                    </thead>
                  </SortableContext>
                </DndContext>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {paged.map(item => {
                    const cells: Record<string, React.ReactNode> = {
                      job_ref: (
                        <td key="job_ref" className="px-3 py-2.5 font-mono text-xs text-blue-700 dark:text-blue-400 font-semibold whitespace-nowrap">
                          {item.job_ref || '—'}
                          {item.job_ref && incompleteJobRefs.has(item.job_ref) && (
                            <span title="Not every row of this job has left the warehouse yet (collected / in transit / completed)"
                              className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 align-middle">
                              Incomplete {incompleteJobRefs.get(item.job_ref)!.done}/{incompleteJobRefs.get(item.job_ref)!.total} rows
                            </span>
                          )}
                        </td>
                      ),
                      house_refs: (
                        <td key="house_refs" className="px-3 py-2.5 font-mono text-xs text-indigo-600 dark:text-indigo-400">
                          <div className="whitespace-pre-wrap leading-4">{item.house_job_refs || '—'}</div>
                        </td>
                      ),
                      destination: (
                        <td key="destination" className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 max-w-[160px]">
                          <div className="truncate" title={item.delivery_address || ''}>
                            {item.destination || item.delivery_address || '—'}
                          </div>
                        </td>
                      ),
                      split: (
                        <td key="split" className="px-3 py-2.5 text-xs font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                          {item.group_name || '—'}
                        </td>
                      ),
                      nm: (
                        <td key="nm" className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 max-w-[180px]">
                          <div className="whitespace-pre-wrap leading-4">{item.system_ref || '—'}</div>
                        </td>
                      ),
                      mode: (
                        <td key="mode" className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {item.delivery_method || '—'}
                        </td>
                      ),
                      plts: (
                        <td key="plts" className="px-3 py-2.5 text-xs font-semibold font-mono text-center text-purple-700 dark:text-purple-400">
                          {item.pallets_qty ?? '—'}
                        </td>
                      ),
                      ctns: (
                        <td key="ctns" className="px-3 py-2.5 text-xs font-semibold font-mono text-center text-blue-700 dark:text-blue-400">
                          {item.cartons}
                        </td>
                      ),
                      wt: (
                        <td key="wt" className="px-3 py-2.5 text-xs font-mono text-right text-gray-600 dark:text-gray-300">
                          {item.kgs != null ? item.kgs.toFixed(1) : '—'}
                        </td>
                      ),
                      cbm: (
                        <td key="cbm" className="px-3 py-2.5 text-xs font-mono text-right text-gray-600 dark:text-gray-300">
                          {item.cbm != null ? item.cbm.toFixed(2) : '—'}
                        </td>
                      ),
                      note: (
                        <td key="note" className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 max-w-[120px]">
                          <div className="truncate" title={item.notes || ''}>
                            {item.notes || (item.release_note_ref ? (
                              <button onClick={() => downloadNote(item)}
                                className="text-blue-600 hover:underline dark:text-blue-400 font-mono">
                                {item.release_note_ref} ↓
                              </button>
                            ) : '—')}
                          </div>
                          {item.notes && item.release_note_ref && (
                            <button onClick={() => downloadNote(item)}
                              className="text-blue-600 hover:underline dark:text-blue-400 font-mono text-[10px]">
                              {item.release_note_ref} ↓
                            </button>
                          )}
                        </td>
                      ),
                      status: (
                        <td key="status" className="px-3 py-2.5 whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CHIP[item.status] || 'bg-gray-100 text-gray-500'}`}>
                            {item.status === 'in_inbound' ? 'In Inbound' : 'Pending Release'}
                          </span>
                        </td>
                      ),
                      dispatched: (
                        <td key="dispatched" className="px-3 py-2.5 whitespace-nowrap">
                          {progressByRow[item.id] && (item.devanning_id ? (
                            <button onClick={() => setDrawerRowId(item.id)} title="Open this row's notes">
                              <DispatchStatusBadge progress={progressByRow[item.id]} />
                            </button>
                          ) : <DispatchStatusBadge progress={progressByRow[item.id]} />)}
                        </td>
                      ),
                    }
                    const days = daysSince(item.inbound_at)
                    // Only rows still needing action get the warning; Planned/finished rows are already
                    // arranged, so their day count stays grey and the row keeps a calm background.
                    const stage = stageByRow[item.id]
                    const inboundLevel = classifyOverdue(days, { dangerAtDays: 8, severeAtDays: 15 }, stage === 'action')
                    const rowCls = inboundLevel !== 'ok' ? OVERDUE_ROW_CLASS[inboundLevel]
                      : stage === 'done' ? 'bg-green-50/50 dark:bg-green-900/10 hover:bg-green-50 dark:hover:bg-green-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    // Merged single-note model: a note isn't 1:1 with an item any more (several
                    // ACTIVE notes across different hauliers/days can cover overlapping items),
                    // so the button is a dropdown listing every ACTIVE one (View opens its
                    // stored pdf_url directly) plus a "+ New" entry that always navigates to
                    // the one Notes.tsx selection UI.
                    const rowIds = rowItemIds(item)
                    const notesForRow = activeNotes.filter(n => noteMatchesSource(n, rowIds))
                    const canOpenNotes = !!item.devanning_id
                    return (
                      <tr key={item.id} id={`inbound-row-${item.id}`} className={`${rowCls} ${highlightRow === item.id ? 'ring-2 ring-inset ring-blue-400 bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                        {visibleCols.map(col => cells[col.id])}
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {days != null ? (
                            <span className={`text-xs font-mono ${OVERDUE_TEXT_CLASS[inboundLevel]}`}>
                              {inboundLevel !== 'ok' ? overdueBadgeText(days, inboundLevel) : `${days}d`}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="flex gap-1.5 items-center">
                            {canOpenNotes ? (
                              <button onClick={() => setDrawerRowId(item.id)} title="Open this row's notes"
                                className={`px-2 py-1 text-xs rounded-md font-medium whitespace-nowrap ${NOTE_CHIP}`}>
                                Note ({notesForRow.length})
                              </button>
                            ) : (
                              <span title="Not linked to a devanning" className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-400 rounded-md font-medium whitespace-nowrap cursor-not-allowed">Note</span>
                            )}
                            <button onClick={() => openCancel(item)}
                              className="px-2 py-1 text-xs text-gray-400 hover:text-red-500 rounded-md">
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {visible.length === 0 && (
                    <tr><td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-gray-400">
                      No inbound items matching current filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {!loading && visible.length > 0 && (
            <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={pagedTotal} />
          )}
        </div>
      </div>
      <StickyHScrollbar {...ibScrollBar} />

      {/* ── Row notes drawer ───────────────────────────────────────────────── */}
      {(() => {
        const row = drawerRowId ? items.find(i => i.id === drawerRowId) : undefined
        if (!row) return null
        const notesLink = row.devanning_id ? `/warehouse/${id}/devannings/${row.devanning_id}/notes` : null
        // Back path reopens THIS drawer on THIS row (?row scrolls/highlights, ?notes=1 reopens).
        const back = { path: `/warehouse/${id}/inbound?row=${row.id}&notes=1`, label: `Inbound · ${row.house_job_refs || row.job_ref || row.group_name || 'row'}` }
        const ids = rowItemIds(row)
        return (
          <RowNotesDrawer
            row={{ jobRef: row.job_ref, houseRef: row.house_job_refs, group: row.group_name, destination: row.delivery_address || row.destination,
              mode: row.delivery_method, plts: row.pallets_qty, ctns: row.cartons, kgs: row.kgs, cbm: row.cbm }}
            notes={activeNotes.filter(n => noteMatchesSource(n, ids))}
            progress={progressByRow[row.id]}
            newNoteLink={notesLink && row.devanning_item_id ? withBack(`${notesLink}?item=${row.devanning_item_id}`, back) : null}
            openNoteLink={n => notesLink ? withBack(`${notesLink}?note=${n.id}`, back) : null}
            userId={user?.id}
            onClose={() => setDrawerRowId(null)}
            onChanged={fetchItems}
          />
        )
      })()}

      {/* ── Cancel Item Dialog ─────────────────────────────────────────────── */}
      {dialog === 'cancel_item' && selItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Cancel Inbound Item?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              <strong>{selItem.group_name}</strong> will be marked cancelled and removed from the Inbound list.
              {selItem.release_note_ref && (
                <span className="mt-1 block text-amber-600 dark:text-amber-400 text-xs">
                  Note: {selItem.release_note_ref} draft will remain but the inventory item will be cancelled.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDialog(null)} className={btnSecondary}>Keep</button>
              <button onClick={submitCancel} disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {submitting ? 'Cancelling…' : 'Cancel Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
